import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiIsLoaded } from "@vis.gl/react-google-maps";

import CaseService from "services/Case";
import MachineService from "services/Machine";
import {
  bboxFor,
  machinesToGeoJson,
  plottableAddresses,
  splitByPlottability,
} from "components/CaseMap/helpers";
import { caseMapMachinesKey } from "components/CaseMap/queryKeys";

// The map has no pagination, so it takes one oversized page. That is the real
// cost here: without complete_table the machines query builds the case-event,
// address and distance CTEs, so the case scope narrows the spatial aggregation
// rather than skipping it. The ceiling is what keeps a pathological case bounded.
export const MAP_MACHINE_LIMIT = 1000;

export function useCaseMapMachines({
  caseRouteId,
  filterParams,
  locationSource,
}) {
  const { data: caseModel, isLoading: isLoadingCase } = useQuery({
    queryKey: ["case", caseRouteId],
    queryFn: () => CaseService.get(caseRouteId),
    enabled: !!caseRouteId,
  });

  // An external case_id is unique only within a product line, so the backend
  // matches nothing rather than leak across product lines when it arrives
  // unscoped. Wait for both before asking.
  const isScopeReady = !!caseModel?.case_id && !!caseModel?.product_line_id;

  // caseModel is the result of ["case", caseRouteId], so caseRouteId already
  // determines case_id and product_line_id. Putting them in the key would break
  // useCaseMapStatusChange, which rebuilds this key from the route and the URL
  // alone and cannot see caseModel.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const { data: machinesPage, isFetching: isFetchingMachines } = useQuery({
    queryKey: caseMapMachinesKey(caseRouteId, filterParams),
    queryFn: () =>
      MachineService.getTable({
        // SECURITY: the URL-derived filters are spread first so a hand-edited
        // case_id or product_line_id cannot widen the scope past the case the
        // route resolved.
        ...filterParams,
        case_id: caseModel.case_id,
        product_line_id: caseModel.product_line_id,
        page: 1,
        per_page: MAP_MACHINE_LIMIT,
      }),
    enabled: isScopeReady,
  });

  const isApiLoaded = useApiIsLoaded();
  const isLoading = isLoadingCase || isFetchingMachines || !isApiLoaded;

  const machines = useMemo(() => machinesPage?.data ?? [], [machinesPage]);

  // A case inherits its addresses from the linked entity, and the case details
  // already carry them, so the address pins cost no extra request.
  const addresses = useMemo(
    () => plottableAddresses(caseModel?.addresses),
    [caseModel]
  );

  const { pinned, notOnMap, geoJson, bbox } = useMemo(() => {
    const split = splitByPlottability(machines, locationSource);
    const collection = machinesToGeoJson(split.pinned);
    const addressPositions = addresses.map(address => ({
      lat: address.latitude,
      lng: address.longitude,
    }));

    return {
      ...split,
      geoJson: collection,
      bbox: bboxFor(collection, addressPositions),
    };
  }, [machines, locationSource, addresses]);

  const total = machinesPage?.total ?? machines.length;

  return {
    caseModel,
    machines,
    addresses,
    pinned,
    notOnMap,
    geoJson,
    bbox,
    isLoading,
    // A case bigger than one page is plotted only as far as the limit, and the
    // toolbar has to say so rather than imply a complete picture.
    truncatedTotal: total > machines.length ? total : null,
  };
}
